import {
	ColorV2,
	Modal,
	ProgressBar,
	Text,
	Button,
} from "@adoptaunabuelo/react-components";
import styled from "styled-components";
import DonationShield from "../../assets/images/icon/DonationShield";
import { CheckCircle2, Coins, Lock, X } from "lucide-react";
import { useSelector } from "react-redux";
import { State } from "../../redux";
import media from "styled-media-query";

const ModalContainer = styled.div`
	display: flex;
	flex-direction: row;
	${media.lessThan("medium")`
        flex-direction: column;
	`}
`;
const ColLeft = styled.div`
	display: flex;
	flex-direction: column;
	background-color: ${ColorV2.surface.background};
	width: 250px;
	padding: 40px 32px 32px;
	gap: 16px;
	border-radius: 0px 24px 24px 0px;
	${media.lessThan("medium")`
        width: unset;
        border-radius: 0px 0px 24px 24px;
        padding: 40px 24px 32px;
	`}
`;
const ColRight = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: 40px 32px;
	gap: 16px;
	justify-content: center;
	${media.lessThan("medium")`
        padding: 40px 24px;
	`}
`;
const Col = styled.div`
	display: flex;
	flex-direction: column;
`;
const Row = styled.div`
	display: flex;
	flex-direction: row;
`;
const MainIcon = styled.div`
	height: 64px;
	width: 64px;
	background-color: ${ColorV2.border.primarySoft};
	border-radius: 64px;
	padding: 4px;
`;

const ProtectedDonationModal = (props: ProtectedDonationModalProps) => {
	const isMobile: boolean = useSelector(
		(state: State) => state.screen.isMobile
	);
	const options = [
		{
			icon: <Lock color={ColorV2.text.neutralHard} />,
			title: "Pago seguro garantizado",
			subtitle:
				"Tu seguridad es muy importante para nosotros. Utilizamos cifrado SSL para garantizar que tus datos estén completamente seguros y protegidos durante el proceso de pago.",
		},
		...(props.type === "donation"
			? [
				{
					icon: <Coins color={ColorV2.text.neutralHard} />,
					title: "Qué hacemos con tu donación",
					subtitle: "Nos comprometemos a destinar tu donación a programas de acompañamiento para las personas mayores.",
					distribution: [
						{
							title: "90% Actividades con mayores",
							color: ColorV2.border.primary,
							value: 90,
						},
						{
							title: "8% Costes operacionales",
							color: ColorV2.border.neutralHigh,
							value: 8,
						},
						{
							title: "2% Comisiones bancarias",
							color: ColorV2.border.secondary,
							value: 2,
						},
					],
				},
			]
			: props.type === "challenge"
				? [
					{
						icon: <Coins color={ColorV2.text.neutralHard} />,
						title: "A qué destinamos tu dinero",
						subtitle:
							"Todo el dinero recaudado se destina de forma íntegra a realizar sueño.",
					},
					{
						icon: <CheckCircle2 color={ColorV2.text.neutralHard} />,
						title: "Qué ocurre si se recauda más dinero del objetivo",
						subtitle:
							"El importe extra recaudado será destinado completamente a los programas de acompañamiento a personas mayores que desarrollamos desde Adopta Un Abuelo. Por cada 1,5€ recaudado se realiza una hora de acompañamiento.",
					},
				]
				: props.type === "birthday"
					? [
						{
							icon: <Coins color={ColorV2.text.neutralHard} />,
							title: "A qué destinamos tu dinero",
							subtitle:
								"Todo el dinero recaudado se destina de forma íntegra al cumpleaños.",
						},
						{
							icon: <CheckCircle2 color={ColorV2.text.neutralHard} />,
							title: "Qué ocurre si se recauda más dinero del objetivo",
							subtitle:
								"El importe extra recaudado será destinado completamente a los programas de acompañamiento a personas mayores que desarrollamos desde Adopta Un Abuelo. Por cada 1,5€ recaudado se realiza una hora de acompañamiento.",
						},
					]
					: []),
	];

	return (
		<Modal
			isVisible={props.isVisible}
			style={{ width: 820 }}
			type={isMobile ? "full-screen" : "default"}
			contentStyle={{ padding: 0 }}
			hideClose={true}
			onClose={props.onClose}
		>
			<ModalContainer>
				<ColLeft>
					<MainIcon>
						<DonationShield size="large" />
					</MainIcon>
					<Text type="h3" weight="semibold">
						Tu donación es segura
					</Text>
					<Text type="p">
						Adopta Un Abuelo es una fundación registrada en el
						Ministerio de Justicia con el nº de registro 2796 y CIF
						G56246788
					</Text>
				</ColLeft>
				<ColRight>
					<Button
						style={{
							position: "absolute",
							top: 8,
							right: 8,
							backgroundColor: "white",
							borderRadius: 48,
						}}
						design={"image"}
						icon={<X color={ColorV2.text.neutralHard} />}
						onClick={() => props.onClose()}
					/>
					{options.map((item, index) => (
						<Row
							key={`protected-donation-option-${index}`}
							style={{ gap: isMobile ? 8 : 12 }}
						>
							{item.icon}
							<Col style={{ flex: 1 }}>
								<Text type="p" weight="medium">
									{item.title}
								</Text>
								<Text
									type="p2"
									style={{
										color: ColorV2.text.neutralMedium,
									}}
								>
									{item.subtitle}
								</Text>
								{item.distribution && (
									<Col style={{ marginTop: 8 }}>
										{item.distribution.map(
											(value, index) => (
												<Row
													key={`protected-donation-distribution-${index}`}
													style={{
														alignItems: "center",
														gap: 8,
													}}
												>
													<ProgressBar
														style={{
															width: isMobile
																? 100
																: 160,
															flex: "unset",
															backgroundColor:
																ColorV2.surface
																	.background,
														}}
														progress={value.value}
														color={value.color}
													/>
													<Text
														type="c1"
														style={{
															color: ColorV2.text
																.neutralMedium,
														}}
													>
														{value.title}
													</Text>
												</Row>
											)
										)}
									</Col>
								)}
							</Col>
						</Row>
					))}
				</ColRight>
			</ModalContainer>
		</Modal>
	);
};
export default ProtectedDonationModal;
export type ProtectedDonationModalProps = {
	isVisible: boolean;
	onClose: () => void;
	type: "donation" | "challenge" | "birthday";
};
